import React from "react";
import { motion } from "framer-motion";
import { Pill, FlaskConical, Hospital, ScanLine, Ambulance, Leaf, Stethoscope } from "lucide-react";

const ventures = [
    {
        icon: Stethoscope,
        name: "One Rupee Clinic",
        tag: "Founding venture",
        text: "Primary & emergency care for ₹1. 33 railway EMRs and clinics across Mumbai's commute lines.",
        color: "bisleri",
    },
    {
        icon: Pill,
        name: "Kamal Pharma",
        tag: "Discount pharmacy chain",
        text: "Branded & generic medicines at ~50% reduction. One pharmacy attached to every clinic.",
        color: "clinicgreen",
    },
    {
        icon: FlaskConical,
        name: "Kamal Pathology Lab",
        tag: "ISO-Certified Laboratory",
        text: "Same-day diagnostics — Blood Sugar, CBC, Cholesterol, Creatinine, Bilirubin, Calcium, Urine.",
        color: "bisleri",
    },
    {
        icon: Hospital,
        name: "Kamal Hospital",
        tag: "ICU · Operation · Multispecialty",
        text: "Full-spectrum hospital care — ICU, OT, multispecialty consults, 2,800+ beds across the group.",
        color: "clinicgreen",
    },
    {
        icon: ScanLine,
        name: "Kamal CT Scan Centre",
        tag: "Advanced imaging",
        text: "Affordable CT imaging, accessible to the same patients walking in from the railway platform.",
        color: "bisleri",
    },
    {
        icon: Ambulance,
        name: "Kamal Ambulance Service",
        tag: "Golden Hour transport",
        text: "Rapid response and inter-facility transfer — closing the loop between EMR and full hospital care.",
        color: "clinicgreen",
    },
    {
        icon: Leaf,
        name: "Swasthyamrut",
        tag: "Ayurvedic & Panchakarma Centre",
        text: "Traditional Ayurvedic medicine and Panchakarma — chikitsalaya for holistic, accessible wellness.",
        color: "bisleri",
    },
];

const Ventures = () => {
    return (
        <section
            id="ventures"
            data-testid="ventures-section"
            className="relative py-24 md:py-32 lg:py-40 bg-cream border-y border-ink-900/8"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="section-number">04 / Ventures</div>
                        <div className="mt-6 divider" />
                        <div className="mt-4 eyebrow">Magicdil Group of Companies</div>
                    </div>

                    <div className="col-span-12 lg:col-span-8">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink-900 max-w-3xl"
                        >
                            One promise. <em className="text-bisleri">Seven ventures.</em>
                            <br />A single ecosystem of care.
                        </motion.h2>
                        <p className="mt-6 font-body text-base md:text-lg text-ink-700 max-w-2xl leading-relaxed">
                            What started as one clinic on one platform now spans pharmacies,
                            pathology labs, hospitals, CT imaging, ambulance services and
                            Ayurvedic medicine — all stitched together under one mission of
                            quality, affordable healthcare.
                        </p>
                    </div>
                </div>

                <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {ventures.map((v, i) => {
                        const Icon = v.icon;
                        const isGreen = v.color === "clinicgreen";
                        return (
                            <motion.div
                                key={v.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.9, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                                className="group p-8 md:p-9 bg-paper-50 border border-ink-900/10 hover:border-bisleri/40 hover:shadow-xl hover:shadow-bisleri/5 hover:-translate-y-1 transition-all duration-500"
                                data-testid={`venture-${i}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${isGreen ? "bg-clinicgreen-soft text-clinicgreen-deep" : "bg-bisleri-light text-bisleri"}`}>
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-500">
                                        0{i + 1}
                                    </div>
                                </div>

                                <h3 className="mt-6 font-display text-2xl md:text-3xl text-ink-900 leading-tight">
                                    {v.name}
                                </h3>
                                <div className={`mt-2 font-mono text-[11px] tracking-[0.2em] uppercase ${isGreen ? "text-clinicgreen-deep" : "text-bisleri"}`}>
                                    {v.tag}
                                </div>
                                <p className="mt-5 font-body text-sm md:text-base text-ink-700 leading-relaxed">
                                    {v.text}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Ventures;
